import { useEffect, useMemo, useState } from "react";
import { FormProvider, useForm as useReactHookForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";

import FormInputs from "@/components/Organisms/HookFormInputs/V1";
import Typography from "@/foundation/Typography/V2";

import TrialCallbackSection from "../TrialCallbackSection";

import { classNameGenerator } from "@/utils";

import { useAppSelector } from "@/hooks/useRedux";

import styles from "./BookSlotForm.module.scss";

export const formSchema = (defaultValues = {}) => {
  const keys = Object.keys(defaultValues);

  return yup
    .object({
      ...(keys?.includes("selectedOneOnOneYogaDate") && {
        selectedOneOnOneYogaDate: yup.string().required("Please select a date"),
      }),
      ...(keys?.includes("selectedOneOnOneYogaSlot") && {
        selectedOneOnOneYogaSlot: yup.string().required("Please select a slot"),
      }),
    })
    .required();
};

interface BookSlotFormProps {
  setSlot?: (data: any) => void;
  setShowSlotModal?: (showSlotModal: boolean) => void;
}

const BookSlotForm: React.FC<BookSlotFormProps> = ({
  setSlot = () => {},
  setShowSlotModal = () => {},
}) => {
  const { slotsData } = useAppSelector((state) => state.bootCamp);

  const [availableDates, setAvailableDates] = useState([]);
  const [availableSlots, setAvailableSlots] = useState([]);

  const cls = classNameGenerator(styles);

  const defaultValues = useMemo(
    () => ({
      selectedOneOnOneYogaDate: "",
      selectedOneOnOneYogaSlot: "",
    }),
    []
  );

  const resolver = useMemo(
    () => yupResolver(formSchema(defaultValues)),
    [defaultValues]
  );

  const methods = useReactHookForm({
    resolver,
    defaultValues,
    mode: "onChange",
  });

  const { reset, watch, setValue } = methods;

  const selectedOneOnOneYogaDate = watch("selectedOneOnOneYogaDate");

  useEffect(() => {
    const _availableDates = slotsData || [];
    setAvailableDates(_availableDates);
  }, [slotsData]);

  useEffect(() => {
    setValue("selectedOneOnOneYogaSlot", "");
  }, [selectedOneOnOneYogaDate, setValue]);

  useEffect(() => {
    const _availableSlots =
      availableDates?.find(({ date }) => date === selectedOneOnOneYogaDate)
        ?.slots || [];
    setAvailableSlots(_availableSlots);
  }, [availableDates, selectedOneOnOneYogaDate]);

  useEffect(() => {
    setValue("selectedOneOnOneYogaDate", availableDates?.[0]?.date);
  }, [availableDates, setValue]);

  useEffect(() => {
    reset(defaultValues);
  }, [reset, defaultValues]);

  const resetData = () => reset(defaultValues);

  const { handleSubmit } = methods;

  const onSubmit = handleSubmit((formValues) => {
    setSlot(formValues);
    resetData();
  });

  const components = [
    {
      name: "yogaTrialSelectDate",
      props: {
        name: "selectedOneOnOneYogaDate",
        availableDates,
        rules: { required: "Please select a date" },
      },
    },
    {
      name: "yogaTrialSelectSlot",
      props: {
        name: "selectedOneOnOneYogaSlot",
        availableSlots,
        rules: { required: "Please select a slot" },
      },
    },
    {
      props: {
        children: <TrialCallbackSection setShowSlotModal={setShowSlotModal} />,
      },
    },
    {
      name: "submit",
      props: {
        children: (
          <Typography
            variant="span"
            dangerouslySetInnerHTML={{
              __html: `Confirm slot`,
            }}
          />
        ),
        className: cls("submitButton"),
      },
    },
    {
      props: {
        children: (
          <Typography
            variant="paragraph1"
            className={cls("infoText")}
            dangerouslySetInnerHTML={{
              __html:
                "Once enrolled, download the Shyft app to attend your class",
            }}
          />
        ),
      },
    },
  ];

  return (
    <FormProvider {...methods}>
      <form className={cls("formWrapper")} onSubmit={onSubmit}>
        <FormInputs components={components} />
      </form>
    </FormProvider>
  );
};

export default BookSlotForm;
